import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, DatePicker, Form, message } from 'antd';
import moment from 'moment';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { getDateOnlyStartOfDayFromBEDateString } from '../../utils/DataFormatterUtils';

interface BudgetVariablePeriodCycleFieldProps {
	startDate: string;
	form: any;
	initialValue: any[];
}

export const allCyclesCustomValidator = (form) => {
	const allCycles = form.getFieldValue('allCycles') || [];
	const fieldUpdates = [];

	allCycles.forEach((cycle, idx) => {
		const path = ['allCycles', idx, 'value'];
		const val = cycle && cycle.value;
		let msg: string | null = null;

		if (!Array.isArray(val) || val.length !== 2 || !val[0] || !val[1]) {
			msg = 'Please select both start and end dates';
		} else if (val[1].isBefore(val[0], 'day')) {
			msg = 'End date must be after start date';
		}

		fieldUpdates.push({
			name: path,
			errors: msg ? [msg] : [],
		});
	});

	form.setFields(fieldUpdates);

	return fieldUpdates.some((f) => f.errors && f.errors.length);
};

const BudgetVariablePeriodCycleField: FC<BudgetVariablePeriodCycleFieldProps> = ({
	startDate,
	form,
	initialValue,
}): React.ReactElement => {
	const { setFieldsValue } = form;

	const startMoment = useMemo(() => moment(startDate).clone(), [startDate]);

	const allCycles = Form.useWatch('allCycles', form);

	const parsedInitialValue = useMemo(
		() =>
			(initialValue || []).map((c) => ({
				value: [
					c.startDate ? getDateOnlyStartOfDayFromBEDateString(c.startDate) : null,
					c.endDate ? getDateOnlyStartOfDayFromBEDateString(c.endDate) : null,
				],
			})),
		[initialValue]
	);

	useEffect(() => {
		const existingValue = nullSafeGetOrElse('0', allCycles, {});

		const [currentStartDate, _] = existingValue.value || [];

		if (currentStartDate && moment(currentStartDate).isSame(startMoment, 'day')) return;

		const firstPeriodOfInitialValue = nullSafeGetOrElse('0.value', parsedInitialValue, []);

		const [initialValueStartDate, __] = firstPeriodOfInitialValue;

		const useInitialValue =
			!initialValueStartDate || moment(initialValueStartDate).isSame(startMoment, 'day');

		setFieldsValue({
			allCycles:
				nullSafeGet('0', parsedInitialValue) && useInitialValue
					? parsedInitialValue
					: [{ value: [startMoment, null] }],
		});
	}, [allCycles, parsedInitialValue, setFieldsValue, startMoment]);

	useEffect(() => {
		if (!allCycles) return;

		const newIndex = allCycles.length - 1;
		const newValue = allCycles[newIndex];
		if (!nullSafeGet('value.0', newValue)) {
			//Start date for the newly added range is not set yet
			const previousValue = allCycles[allCycles.length - 2];
			if (!previousValue || !previousValue.value) return;

			const [, lastEnd] = previousValue.value || [];
			if (!lastEnd || !moment.isMoment(lastEnd)) return;

			const nextStart = moment(lastEnd).add(1, 'day');

			const updatedCycles = allCycles.map((cycle, index) =>
				index === newIndex ? { ...cycle, value: [nextStart, null] } : cycle
			);

			setFieldsValue({ allCycles: updatedCycles });
		}
	}, [allCycles, setFieldsValue]);

	const onAddCycle = useCallback(
		(add) => () => {
			const lastCycle = allCycles[allCycles.length - 1];

			if (!lastCycle) return;

			const [, lastEnd] = lastCycle.value || [];
			if (!lastEnd) {
				message.error('Please select an end date for the last period before adding a new one.');
				return;
			}

			add();
		},
		[allCycles]
	);

	return (
		<Form.List name={'allCycles'}>
			{(fields, { add, remove }) => (
				<>
					{fields.map(({ key, name, ...restField }, index) => (
						<div
							key={key}
							className="flex w-full flex-row flex-nowrap items-baseline justify-between"
						>
							<div>{`Period ${index + 1}`}</div>
							<div className="flex flex-row items-baseline gap-2">
								<Form.Item
									{...restField}
									style={{ marginBottom: '8px' }}
									name={[name, 'value']}
									valuePropName="value"
									getValueFromEvent={(value) => value}
									rules={[
										{
											validator: async (_, value) => {
												if (!value || value.length !== 2) {
													throw new Error('Please select both start and end dates');
												}

												const [start, end] = value;

												if (!start || !end) {
													throw new Error('Please select both start and end dates');
												}

												if (end.isBefore(start, 'day')) {
													throw new Error('End date must be after start date');
												}
											},
										},
									]}
									preserve={false}
								>
									<DatePicker.RangePicker
										style={{ width: '100%' }}
										disabled={[true, index !== fields.length - 1]}
										showTime={false}
									/>
								</Form.Item>
								<DeleteOutlined
									style={{
										cursor: 'pointer',
										visibility: index === 0 || index !== fields.length - 1 ? 'hidden' : 'visible',
									}}
									onClick={() => remove(name)}
								/>
							</div>
						</div>
					))}
					<Form.Item>
						<Button type="link" onClick={onAddCycle(add)} icon={<PlusOutlined />}>
							Add Period
						</Button>
					</Form.Item>
				</>
			)}
		</Form.List>
	);
};

export default BudgetVariablePeriodCycleField;
